import { AfterContentInit, ContentChildren, Directive, ElementRef, Input, OnDestroy, OnInit, QueryList } from '@angular/core';
import { NavigationTableInputDirective } from './navigation-table-input.directive';
import { Observable, Subscription } from 'rxjs';
import { NavigationTableInputDirectiveEvent } from './navigation-table-input-directive-event';


@Directive({
	standalone: true,
	selector: 'p-table[appNavigationTable], p-treeTable[appNavigationTable]'
})
export class NavigationTableDirective implements OnInit, AfterContentInit, OnDestroy {

	// Liste des directives NavigationTableInputDirective
	@ContentChildren(NavigationTableInputDirective, { descendants: true })
	navigationTableInputDirectives: QueryList<NavigationTableInputDirective>;

	// Liste des observables à écouter pour réagir à un changement de données dans le tableau
	@Input()
	appNavigationTable: Observable<any>[];

	// Map avec les éléments html par colonne
	elementsHtmlParColonne: Map<string, ElementRef[]>;

	souscriptions: Subscription[] = [];

	constructor(private elementRef: ElementRef) {
	}

	ngOnInit(): void {
		// On boucle sur les observables pour souscrire à chacun d'eux afin de réagir à leur changement
		this.appNavigationTable.forEach(observable => {
			const souscription = observable.subscribe(() => {
				// On ajoute un léger délai avec setTimeout pour être sûr que les directives soient initialisées
				setTimeout(() => {
					// On crée une map avec les éléments du tableau en clé et les directives en valeur
					const directiveParElement = new Map<ElementRef, NavigationTableInputDirective>();
					this.navigationTableInputDirectives.forEach(directive => {
						directiveParElement.set(directive.elementRef.nativeElement, directive);
					});

					// Met à jour la liste des éléments html par colonne
					this.elementsHtmlParColonne = this.rechercherNavigationTableInput().reduce((map, element) => {
						// On récupère la directive associée à l'élément html
						const directive = directiveParElement.get(element);
						// Si la directive n'existe pas, on ne fait rien
						if (!directive) {
							return map;
						}
						const elementsPourColonne = map.get(directive.appNavigationTableInput) ?? [];
						// On récupère la liste des éléments html pour la colonne de la directive si elle existe, sinon on la crée
						elementsPourColonne.push(directive.elementRef);
						return map.set(directive.appNavigationTableInput, elementsPourColonne);
					}, new Map<string, ElementRef[]>());
				}, 100);
			});
			this.souscriptions.push(souscription);
		});
	}

	/**
	 * Recherche les éléments du tableau portant l'attribut navigationTableInput
	 */
	rechercherNavigationTableInput(): ElementRef[] {
		return Array.prototype.slice.call(this.elementRef.nativeElement.querySelectorAll('[navigationTableInput]'));
	}

	/**
	 * Permet de mettre le focus sur l'élément html suivant ou précédent en fonction de la touche fléchée haut ou bas
	 * @param elementRef l'élément sur lequel on a appuyé sur la touche
	 * @param nomColonne le nom de la colonne sur laquelle on a appuyé sur la touche
	 * @param ligneSuivante true si l'on doit sélectionner sur une ligne suivante, false si on doit sélectionner sur une ligne précédente
	 */
	mettreFocusSurElementHtml(elementRef: ElementRef, nomColonne: string, ligneSuivante: boolean): void {
		const inputs = this.elementsHtmlParColonne.get(nomColonne);
		const index = inputs.findIndex(inputCourant => inputCourant === elementRef);

		// Si l'index est égal à la dernière ligne du tableau ou à la premiere ligne du tableau.
		if (index == null || (index === inputs.length - 1 && ligneSuivante) || (index === 0 && !ligneSuivante)) {
			// On perd mystérieusement le focus sur la cellule initiale aléatoirement donc on réapplique le focus sur celle-ci au besoin
			elementRef.nativeElement.focus();
			return;
		}
		let indexASelectionner = index;
		let input;
		// On boucle sur les inputs pour trouver le prochain input qui n'est pas disabled et qui n'est pas le dernier ni le premier input
		do {
			indexASelectionner = ligneSuivante ? indexASelectionner + 1 : indexASelectionner - 1;
			input = inputs[indexASelectionner];
		}
		while (input.nativeElement.disabled && indexASelectionner !== inputs.length - 1 && indexASelectionner !== 0);

		// On perd mystérieusement le focus sur la cellule initiale aléatoirement donc on réapplique le focus sur celle-ci au besoin
		const inputAFocus = input && !input.nativeElement.disabled ? input.nativeElement : elementRef.nativeElement;
		inputAFocus.focus();
	}

	ngAfterContentInit(): void {
		// On boucle sur les directives lorsque la liste des directives se met à jour,
		// pour souscrire à l'événement onFlecheAppuyee et appeler la méthode mettreFocusSurElementHtml à chaque fois
		this.souscriptions.push(this.navigationTableInputDirectives.changes.subscribe(() => {
			this.navigationTableInputDirectives.forEach(directive => {
				this.souscriptions.push(directive.onFlecheAppuyee.subscribe((event: NavigationTableInputDirectiveEvent) =>
					this.mettreFocusSurElementHtml(directive.elementRef, directive.appNavigationTableInput, event.suivantBas)
				));
			});
		}));
	}

	ngOnDestroy(): void {
		this.souscriptions.forEach(souscription => souscription.unsubscribe());
	}
}
